'use client';
import { useState } from 'react';
import type { StockFactors } from '@/lib/compute';
import { VALUATION_FACTORS } from '@/lib/clientScore';
import { FactorRow } from './FactorRow';

interface Props {
  enabledFactors: Set<keyof StockFactors>;
  onToggleFactor: (key: keyof StockFactors) => void;
  highlightedFactor: keyof StockFactors | null | undefined;
  onFactorHighlight?: (key: keyof StockFactors) => void;
  infoKey: string | null;
  setInfoKey: (k: string | null) => void;
  /** Canonical V-factor count from data/model-stats.json (14 today: 11
   *  peer-relative + 3 absolute). Used in the panel header so the badge
   *  matches the deck. */
  canonicalCount?: number;
}

// Steve directive 2026-05-08: V is now blended (production commit 82c8b2e):
//   75% × 9 peer-z multiples + 25% × 3 universe-z absolute lenses
// Display this structurally — like Quality groups by pillar, V groups by
// scoring lens. The 9 peer-z multiples are toggleable individually as before.
// The 3 absolute lenses are computed downstream of factor scoring (in
// target-prices.json) and aren't toggleable here — shown for transparency.
const ABSOLUTE_LENSES: Array<{ key: string; label: string; tooltip: string }> = [
  { key: 'asym_z', label: 'Asymmetry (z)', tooltip: '|upside| / |downside| based on bull/bear cases from EPS × multiple, universe-wide z-scored' },
  { key: 'dcf_disc_z', label: 'DCF discount (z)', tooltip: 'DCF fair value vs current price, universe-wide z-scored. CAPM-calibrated discount rate per stock.' },
  { key: 'today_disc_z', label: 'Today FV discount (z)', tooltip: 'Multiple-based fair value vs current price (FY1 EPS × target multiple), universe-wide z-scored' },
];

export function ValuationFactorsPanel({
  enabledFactors,
  onToggleFactor,
  highlightedFactor,
  onFactorHighlight,
  infoKey,
  setInfoKey,
  canonicalCount,
}: Props) {
  const [open, setOpen] = useState(false);

  const togglePeerZAll = () => {
    const allEnabled = VALUATION_FACTORS.every(f => enabledFactors.has(f.key));
    for (const f of VALUATION_FACTORS) {
      if (allEnabled) {
        if (enabledFactors.has(f.key)) onToggleFactor(f.key);
      } else {
        if (!enabledFactors.has(f.key)) onToggleFactor(f.key);
      }
    }
  };

  const allPeerZEnabled = VALUATION_FACTORS.every(f => enabledFactors.has(f.key));
  const somePeerZEnabled = VALUATION_FACTORS.some(f => enabledFactors.has(f.key));

  return (
    <div className="px-5 py-2 border-b border-[#1e293b]">
      <button onClick={() => setOpen(!open)} className="flex items-center justify-between w-full cursor-pointer">
        <label className="text-[#e2e8f0] text-[10px] font-semibold uppercase tracking-widest cursor-pointer">
          Valuation Factors
          {canonicalCount != null && (
            <span className="text-[#64748b] ml-2 normal-case tracking-normal font-normal">
              {canonicalCount} ({Math.max(0, canonicalCount - ABSOLUTE_LENSES.length)} + {ABSOLUTE_LENSES.length})
            </span>
          )}
        </label>
        <svg className={`w-3 h-3 text-white transition-transform duration-150 ${open ? 'rotate-180' : ''}`}
          fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <div className="mt-3 space-y-3">
          {/* Group 1: 9 peer-z multiples (75% weight) */}
          <div>
            <button onClick={togglePeerZAll} className="flex items-center gap-1.5 mb-1 cursor-pointer">
              <span className={`w-2.5 h-2.5 rounded-sm border flex items-center justify-center text-[7px]
                              ${allPeerZEnabled ? 'bg-blue-500 border-blue-500 text-white' :
                                somePeerZEnabled ? 'bg-blue-500/30 border-blue-500/50 text-white' :
                                'border-[#334155]'}`}>
                {allPeerZEnabled && '✓'}
                {somePeerZEnabled && !allPeerZEnabled && '—'}
              </span>
              <span className="text-[10px] font-semibold text-[#e2e8f0]">Peer-z multiples</span>
              <span className="text-[9px] text-amber-400/80 font-mono ml-auto">75%</span>
            </button>
            <div className="ml-4 flex flex-col gap-0">
              {VALUATION_FACTORS.map(f => (
                <FactorRow
                  key={f.key}
                  factorKey={f.key}
                  enabled={enabledFactors.has(f.key)}
                  onToggle={() => onToggleFactor(f.key)}
                  highlighted={highlightedFactor === f.key}
                  onHighlight={() => onFactorHighlight?.(f.key as keyof StockFactors)}
                  infoOpen={infoKey === f.key}
                  onToggleInfo={() => setInfoKey(infoKey === f.key ? null : f.key)}
                />
              ))}
            </div>
          </div>

          {/* Group 2: 3 universe-z absolute lenses (25% weight). Read-only — these
              are computed from target-prices.json (asymmetry, DCF, today FV) at
              the universe-z step downstream of per-factor scoring. */}
          <div>
            <div className="flex items-center gap-1.5 mb-1">
              <span className="w-2.5 h-2.5 rounded-sm bg-amber-500/30 border border-amber-500/50 flex items-center justify-center text-[7px] text-white">✓</span>
              <span className="text-[10px] font-semibold text-[#e2e8f0]">Universe-z absolute</span>
              <span className="text-[9px] text-amber-400/80 font-mono ml-auto">25%</span>
            </div>
            <div className="ml-4 flex flex-col gap-1">
              {ABSOLUTE_LENSES.map(lens => (
                <div
                  key={lens.key}
                  className="flex items-center gap-1.5 text-[10px] text-[#94a3b8]"
                  title={lens.tooltip}
                >
                  <span className="w-2 h-2 rounded-full bg-amber-500/40 flex-shrink-0" />
                  <span>{lens.label}</span>
                </div>
              ))}
            </div>
            <div className="ml-4 mt-1 text-[9px] text-[#475569] italic">
              Computed daily for the full 1099-stock universe. Always-on.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
